import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import ProtectedRoute, {
  PublicOnlyRoute,
  RoleRoute,
} from '@/components/ProtectedRoute';
import DashboardLayout from '@/layouts/DashboardLayout';

const SignIn = lazy(() => import('@/pages/SignIn'));
const NoAccess = lazy(() => import('@/pages/NoAccess'));
const OnboardingInvite = lazy(() => import('@/pages/OnboardingInvite'));

const Agents = lazy(() => import('@/pages/contracting/Agents'));
const NewAgent = lazy(() => import('@/pages/contracting/NewAgent'));
const Carriers = lazy(() => import('@/pages/contracting/Carriers'));
const Reports = lazy(() => import('@/pages/contracting/Reports'));
const Notifications = lazy(() => import('@/pages/contracting/Notifications'));
const Campaigns = lazy(() => import('@/pages/contracting/Campaigns'));
const Birthdays = lazy(() => import('@/pages/contracting/Birthdays'));

function Wrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Public: the invited agent has no session — the token in the URL is the
  // credential. Deliberately outside every guard.
  {
    path: '/onboarding/:token',
    element: (
      <Wrap>
        <OnboardingInvite />
      </Wrap>
    ),
  },
  {
    path: '/sign-in',
    element: (
      <PublicOnlyRoute>
        <Wrap>
          <SignIn />
        </Wrap>
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/sin-acceso',
    element: (
      <Wrap>
        <NoAccess />
      </Wrap>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          // Agentes is the module's home.
          { index: true, element: <Navigate to="/agentes" replace /> },
          {
            path: 'agentes',
            element: (
              <RoleRoute permission="agents.read">
                <Wrap>
                  <Agents />
                </Wrap>
              </RoleRoute>
            ),
          },
          {
            // Formulario manual: la solicitud completa cargada en la oficina.
            path: 'agentes/nuevo',
            element: (
              <RoleRoute permission="agents.create">
                <Wrap>
                  <NewAgent />
                </Wrap>
              </RoleRoute>
            ),
          },
          {
            path: 'carriers',
            element: (
              <RoleRoute permission="carriers.read">
                <Wrap>
                  <Carriers />
                </Wrap>
              </RoleRoute>
            ),
          },
          {
            path: 'reportes',
            element: (
              <RoleRoute permission="reports.read">
                <Wrap>
                  <Reports />
                </Wrap>
              </RoleRoute>
            ),
          },
          {
            path: 'notificaciones',
            element: (
              <RoleRoute permission="notifications.read">
                <Wrap>
                  <Notifications />
                </Wrap>
              </RoleRoute>
            ),
          },
          {
            path: 'campanas',
            element: (
              <RoleRoute permission="campaigns.manage">
                <Wrap>
                  <Campaigns />
                </Wrap>
              </RoleRoute>
            ),
          },
          {
            path: 'cumpleanos',
            element: (
              <RoleRoute permission="birthdays.manage">
                <Wrap>
                  <Birthdays />
                </Wrap>
              </RoleRoute>
            ),
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
